import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable6 = ({ data }) => {
  const pdfRef = useRef();

  const years = ["2018", "2019", "2020", "2021", "2022", "2023", "2024"];

  // --- Color palette (Utah tones) ---
  const palette = [
    "#FFA1B5",
    "#86C7F3",
    "#FFE29A",
    "#93D9D9",
    "#C2A3FF",
    "#FFC58C",
    "#B9D2AA",
    "#6794D1",
  ];

  // ---- Compute Summary Metrics ----
  const totals = years.map((year) =>
    data.reduce((sum, row) => sum + Number(row[`year_${year}`]), 0)
  );

  const peakValue = Math.max(...totals);
  const peakYear = years[totals.indexOf(peakValue)];

  const growthPercent = (
    ((totals.at(-1) - totals[0]) / totals[0]) *
    100
  ).toFixed(1);

  const topAgeGroup = data.reduce(
    (max, group) => {
      const sum = years.reduce((s, y) => s + Number(group[`year_${y}`]), 0);
      return sum > max.sum ? { age: group.agegrp_label, sum } : max;
    },
    { age: null, sum: 0 }
  );

  // ---- Chart Dataset ----
  const datasets = data.map((group, i) => ({
    label: group.agegrp_label,
    data: years.map((year) => parseInt(group[`year_${year}`])),
    borderColor: palette[i % palette.length],
    backgroundColor: palette[i % palette.length] + "30",
    borderWidth: 3,
    tension: 0.4,
    pointBackgroundColor: "white",
    pointBorderColor: palette[i % palette.length],
    pointBorderWidth: 2,
    pointRadius: 5,
    fill: true,
  }));

  const chartData = { labels: years, datasets };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📈 Age-based Workforce Trend (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: "Employment Count", font: { size: 14, weight: "bold" } },
      },
      x: { title: { display: true, text: "Year", font: { size: 14, weight: "bold" } } },
    },
  };

  // ---- PDF Export ----
  const downloadPDF = () => {
    const element = pdfRef.current;

    html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const img = canvas.toDataURL("image/png");
      const pdfFile = new jsPDF("p", "mm", "a4");

      const pageWidth = pdfFile.internal.pageSize.width;
      const usableWidth = pageWidth - 10;
      const height = (canvas.height * usableWidth) / canvas.width;

      // Header
      pdfFile.addImage(utahData, "PNG", 60, 6, 30, 18);
      pdfFile.setFontSize(22);
      pdfFile.text("UTahData.org", 100, 20);
      pdfFile.line(10, 32, 200, 32);

      // Body Image
      pdfFile.addImage(img, "PNG", 5, 35, usableWidth, height);

      // Footer
      pdfFile.line(10, 260, 200, 260);
      pdfFile.addImage(utahData, "PNG", 22, 265, 30, 15);
      pdfFile.setFontSize(10);
      pdfFile.text("Built by", 55, 275);
      pdfFile.addImage(bodex, "PNG", 68, 269, 44, 10);
      pdfFile.text("using", 112, 275);
      pdfFile.addImage(kpi, "PNG", 125, 265, 20, 20);
      pdfFile.text("20250916.v3.198", 150, 275);
      pdfFile.text(`Downloaded: ${new Date().toLocaleDateString()}`, 150, 295);
      pdfFile.text(`From: ${window.location.href}`, 10, 295);

      pdfFile.save("employment_by_agegroup.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white shadow-lg rounded-xl">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
          Workforce Distribution by Age Group (2018–2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-blue-100 rounded-xl text-center shadow">
            <p className="text-sm text-gray-600 font-medium">Overall Growth</p>
            <p className="text-2xl font-bold text-blue-800">
              +{growthPercent}%
            </p>
          </div>

          <div className="p-4 bg-orange-100 rounded-xl text-center shadow">
            <p className="text-sm text-gray-600 font-medium">
              Peak Workforce Year
            </p>
            <p className="text-2xl font-bold text-orange-800">{peakYear}</p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl text-center shadow">
            <p className="text-sm text-gray-600 font-medium">Top Age Segment</p>
            <p className="text-xl font-bold text-green-800">
              {topAgeGroup.age}
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2">Age Group</th>
                    {years.map((year) => (
                      <th key={year} className="p-2">
                        {year}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr key={row.agegrp_label} className="hover:bg-gray-100">
                      <td className="font-medium p-2">{row.agegrp_label}</td>
                      {years.map((year) => (
                        <td key={year} className="p-2">
                          {Number(row[`year_${year}`]).toLocaleString("en-US")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[600px] w-full h-96 p-4">
              <Line data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* PDF Export Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 text-white px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          Download as PDF
          <img src={pdf} className="w-[1.5rem]" alt="Download icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable6;
